import { forwardRef, type ForwardedRef, type SelectHTMLAttributes } from 'react';
import { cn } from '../lib/cn';

const LAYOUT_TOKEN = /^(w-|min-w-|max-w-|m-|mx-|my-|mt-|mb-|ml-|mr-)/;

function splitSelectLayout(className?: string) {
  const tokens = (className ?? '').split(/\s+/).filter(Boolean);
  const layout: string[] = [];
  const rest: string[] = [];
  for (const token of tokens) {
    if (LAYOUT_TOKEN.test(token)) layout.push(token);
    else rest.push(token);
  }
  if (!layout.some((token) => /^(w-|min-w-|max-w-)/.test(token))) {
    layout.push('w-full');
  }
  return { layout: layout.join(' '), rest: rest.join(' ') };
}

/**
 * Select — a styled wrapper over the native <select> (accessible, zero-dep).
 * Matches Input's height/border/focus so they line up in a form row.
 * Width/margin classes on `className` land on the wrapper so compact toolbar
 * selects (`w-32`) don't stretch, while form fields default to full width.
 */
export const Select = forwardRef(function Select(
  { className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>,
  ref: ForwardedRef<HTMLSelectElement>,
) {
  const { layout, rest } = splitSelectLayout(className);
  return (
    <div className={cn('relative inline-flex min-w-0', layout)}>
      <select
        ref={ref}
        className={cn(
          'h-9 w-full appearance-none rounded-md border border-input bg-background pl-3 pr-9 text-sm text-foreground shadow-xs transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
          'disabled:cursor-not-allowed disabled:opacity-50',
          rest,
        )}
        {...props}
      >
        {children}
      </select>
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </div>
  );
});
